import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { BulletinBoard } from './BulletinBoard';
import {
  Archive,
  GraduationCap,
  CalendarDays,
  BookOpen,
  Users,
  ShieldCheck,
  ArrowRight,
  ChevronDown,
  Megaphone,
  Clock,
  FileText,
  LayoutDashboard,
} from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface LandingPageProps {
  onGetStarted: () => void;
}

export function LandingPage({ onGetStarted }: LandingPageProps) {
  const { user } = useAuth();
  const [isLoaded, setIsLoaded] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const bulletinRef = useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState([
    { label: 'Archive Documents', value: '—', icon: FileText },
    { label: 'Graduate Programs', value: '—', icon: GraduationCap },
    { label: 'Scheduled Classes', value: '—', icon: Clock },
    { label: 'Active Users', value: '—', icon: Users },
  ]);

  const fetchStats = useCallback(async () => {
    try {
      const { stats: data } = await api.getPublicStats();
      setStats([
        { label: 'Archive Documents', value: data.archiveDocuments.toLocaleString(), icon: FileText },
        { label: 'Graduate Programs', value: data.graduatePrograms.toLocaleString(), icon: GraduationCap },
        { label: 'Scheduled Classes', value: data.scheduledClasses.toLocaleString(), icon: Clock },
        { label: 'Active Users', value: data.activeUsers.toLocaleString(), icon: Users },
      ]);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    setIsLoaded(true);
    fetchStats();
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fetchStats]);

  const scrollToBulletin = () => {
    bulletinRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    {
      icon: LayoutDashboard,
      title: 'Smart Dashboards',
      description:
        'Role-based dashboards for administrators, faculty, and students with real-time data and insights.',
      color: 'from-blue-500 to-blue-600',
      bgLight: 'bg-blue-50',
      textColor: 'text-blue-600',
    },
    {
      icon: Archive,
      title: 'Digital Archive',
      description:
        'Centralized document management with secure storage, folder organization, and access control.',
      color: 'from-indigo-500 to-indigo-600',
      bgLight: 'bg-indigo-50',
      textColor: 'text-indigo-600',
    },
    {
      icon: CalendarDays,
      title: 'Room Scheduling',
      description:
        'Intelligent room assignment and schedule management with conflict detection and calendar view.',
      color: 'from-emerald-500 to-emerald-600',
      bgLight: 'bg-emerald-50',
      textColor: 'text-emerald-600',
    },
    {
      icon: BookOpen,
      title: 'Course Management',
      description:
        'Comprehensive course catalog, subject management, and enrollment tracking for graduate programs.',
      color: 'from-amber-500 to-amber-600',
      bgLight: 'bg-amber-50',
      textColor: 'text-amber-600',
    },
    {
      icon: Users,
      title: 'Faculty & Students',
      description:
        'Complete user management with verification workflows, profile management, and role-based access.',
      color: 'from-purple-500 to-purple-600',
      bgLight: 'bg-purple-50',
      textColor: 'text-purple-600',
    },
    {
      icon: ShieldCheck,
      title: 'Secure & Verified',
      description:
        'Multi-layer security with email verification, admin approval workflow, and audit logging.',
      color: 'from-rose-500 to-rose-600',
      bgLight: 'bg-rose-50',
      textColor: 'text-rose-600',
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* ===================== HERO SECTION ===================== */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0e27] via-[#0d1b3e] to-[#111a45]" />

        {/* Ambient Glow Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute -top-48 -right-48 w-[600px] h-[600px] bg-blue-600/20 rounded-full filter blur-[160px] animate-glow"
            style={{ transform: `translate(${scrollY * 0.015}px, ${scrollY * 0.008}px)` }}
          />
          <div
            className="absolute -bottom-48 -left-48 w-[600px] h-[600px] bg-indigo-500/15 rounded-full filter blur-[160px] animate-glow animation-delay-2000"
            style={{ transform: `translate(${-scrollY * 0.015}px, ${-scrollY * 0.008}px)` }}
          />
          <div
            className="absolute top-1/4 right-1/4 w-[300px] h-[300px] bg-cyan-400/10 rounded-full filter blur-[120px] animate-glow animation-delay-4000"
            style={{ transform: `translate(${scrollY * 0.01}px, ${scrollY * 0.015}px)` }}
          />
        </div>

        {/* Subtle Grid Overlay */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />

        {/* Radial vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.4)_100%)]" />

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 text-center pt-16 sm:pt-0">

          {/* Logo Mark */}
          <div
            className={`mx-auto mb-6 sm:mb-8 transition-all duration-1000 delay-200 ${
              isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
            }`}
          >
            <div className="relative w-18 h-18 sm:w-20 sm:h-20 md:w-22 md:h-22 mx-auto">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl sm:rounded-[18px] rotate-6 opacity-40 blur-sm" />
              <div className="relative w-full h-full bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-600 rounded-2xl sm:rounded-[18px] flex items-center justify-center shadow-2xl shadow-blue-500/25">
                <Archive className="w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 text-white drop-shadow-sm" />
              </div>
            </div>
          </div>

          {/* Title */}
          <h1
            className={`mb-3 sm:mb-4 tracking-tight transition-all duration-1000 delay-300 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold text-white leading-none">
              AIRS
            </span>
          </h1>

          {/* Subtitle */}
          <p
            className={`text-lg sm:text-xl md:text-2xl font-medium bg-gradient-to-r from-blue-300 via-blue-200 to-indigo-300 text-transparent bg-clip-text mb-5 sm:mb-6 tracking-wide transition-all duration-1000 delay-[400ms] ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Archive &middot; Intelligence &middot; Room Scheduling
          </p>

          {/* Accent line */}
          <div
            className={`mx-auto mb-6 sm:mb-8 w-16 sm:w-20 h-px bg-gradient-to-r from-transparent via-blue-400/60 to-transparent transition-all duration-1000 delay-[450ms] ${
              isLoaded ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
            }`}
          />

          {/* Description */}
          <p
            className={`text-sm sm:text-base md:text-lg text-blue-100/60 max-w-xl sm:max-w-2xl mx-auto mb-10 sm:mb-12 leading-relaxed font-light transition-all duration-1000 delay-500 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            A comprehensive platform for Graduate School archive management, intelligent room
            scheduling, and academic coordination — all in one secure, modern system.
          </p>

          {/* CTA Buttons */}
          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-14 sm:mb-16 transition-all duration-1000 delay-[600ms] ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <Button
              onClick={onGetStarted}
              size="lg"
              className="relative bg-white text-slate-900 hover:bg-white/95 font-semibold px-7 sm:px-9 py-5 sm:py-6 text-sm sm:text-base rounded-xl shadow-lg shadow-black/10 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 group w-full sm:w-auto"
            >
              Get Started
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={scrollToBulletin}
              variant="outline"
              size="lg"
              className="border-white/[0.12] bg-white/[0.04] text-white/90 hover:bg-white/[0.08] hover:border-white/20 hover:text-white font-medium px-7 sm:px-9 py-5 sm:py-6 text-sm sm:text-base rounded-xl backdrop-blur-md transition-all duration-300 group w-full sm:w-auto"
            >
              <Megaphone className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-amber-400/80 group-hover:text-amber-300 transition-colors" />
              View Announcements
            </Button>
          </div>

          {/* Stats Row */}
          <div
            className={`grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-3xl mx-auto transition-all duration-1000 delay-700 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {stats.map((stat) => {
              const StatIcon = stat.icon;
              return (
                <div
                  key={stat.label}
                  className="group relative bg-white/[0.03] border border-white/[0.06] rounded-2xl px-4 py-4 sm:py-5 backdrop-blur-sm hover:bg-white/[0.06] hover:border-white/[0.1] transition-all duration-500"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <StatIcon className="w-4 h-4 text-blue-400/70 group-hover:text-blue-300 transition-colors" />
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{stat.value}</div>
                  <div className="text-[10px] sm:text-xs text-blue-200/40 font-medium uppercase tracking-wider mt-1">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 animate-float">
          <button
            onClick={scrollToBulletin}
            className="text-white/25 hover:text-white/60 transition-colors duration-300 p-2"
            aria-label="Scroll to announcements"
          >
            <ChevronDown className="w-6 h-6 sm:w-7 sm:h-7" />
          </button>
        </div>
      </section>

      {/* ===================== FEATURES SECTION ===================== */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <Badge
              variant="outline"
              className="px-4 py-1.5 text-sm text-blue-600 border-blue-200 bg-blue-50 mb-4"
            >
              Platform Features
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need for Graduate School Management
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg">
              From document archiving to intelligent scheduling, AIRS provides a complete suite of
              tools designed for modern graduate education.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => {
              const FeatureIcon = feature.icon;
              return (
                <Card
                  key={idx}
                  className="group border border-gray-200 hover:border-gray-300 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 overflow-hidden"
                >
                  <CardContent className="p-6">
                    <div
                      className={`w-12 h-12 rounded-xl ${feature.bgLight} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                    >
                      <FeatureIcon className={`w-6 h-6 ${feature.textColor}`} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== BULLETIN BOARD SECTION ===================== */}
      <section ref={bulletinRef} className="py-20 md:py-28 bg-gradient-to-b from-white via-gray-50/50 to-white" id="bulletin">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 mb-4">
              <Megaphone className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-700 font-medium">Graduate School Bulletin</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Announcements, Holidays & Class Mode
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg">
              Stay informed with the latest announcements, upcoming holidays, current class mode settings, and important events from the Graduate School.
            </p>
          </div>

          {/* Bulletin Board */}
          <BulletinBoard userRole={user?.role || null} />
        </div>
      </section>

      {/* ===================== CTA SECTION ===================== */}
      <section className="py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 rounded-3xl p-8 md:p-14 text-center overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-60 h-60 bg-blue-500 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" />
              <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-indigo-500 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" />
            </div>

            <div className="relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-blue-200 max-w-lg mx-auto mb-8 text-base md:text-lg">
                Join the Graduate School community. Access your archives, manage your schedule, and
                stay connected.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={onGetStarted}
                  size="lg"
                  className="bg-white text-blue-900 hover:bg-blue-50 font-semibold px-8 py-6 text-base rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 group"
                >
                  Sign In to AIRS
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FOOTER ===================== */}
      <footer className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
                <Archive className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">AIRS</p>
                <p className="text-xs text-gray-500">Archive Intelligent Room Scheduler</p>
              </div>
            </div>
            <p className="text-xs text-gray-400">
              &copy; {new Date().getFullYear()} Graduate School. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* ===================== ANIMATIONS ===================== */}
      <style>{`
        @keyframes glow {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 1; }
          33% { transform: translate(20px, -30px) scale(1.05); opacity: 0.8; }
          66% { transform: translate(-15px, 15px) scale(0.95); opacity: 1; }
        }
        .animate-glow {
          animation: glow 12s infinite ease-in-out;
        }
        @keyframes float {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(-8px); }
        }
        .animate-float {
          animation: float 3s infinite ease-in-out;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
